import React, { useState } from 'react';
import { InventoryResult } from '../../types';
import { api } from '../../services/api';
import StoryCard from '../StoryCard';
import TabNavigation, { TabType } from '../TabNavigation';
import InventorySimulation from '../simulations/InventorySimulation';
import { modelStories } from '../../data/stories';
import { TrendingDown, Package, ArrowUpCircle } from 'lucide-react';

interface InventoryModuleProps {
  data: InventoryResult | null;
  onOptimizationComplete: (data: InventoryResult) => void;
  onLoadingStart: () => void;
}

const InventoryModule: React.FC<InventoryModuleProps> = ({ data, onOptimizationComplete, onLoadingStart }) => {
  const [activeTab, setActiveTab] = useState<TabType>(data ? 'run' : 'case-study');

  const handleOptimize = async () => {
    onLoadingStart();
    const result = await api.optimizeInventory();
    onOptimizationComplete(result);
    setActiveTab('run');
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amount);

  return (
    <div className="space-y-6">
      <div className="section-card">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Inventory Optimization</h2>
        <p className="text-gray-600 mb-4">
          Optimize stock levels using EOQ and (Q,R) policy with ML forecasting
        </p>
        
        <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} />
      </div>

      {/* Case Study Tab - Always show the story */}
      {activeTab === 'case-study' && (
        <div className="section-card">
          <StoryCard story={modelStories.inventory} />
        </div>
      )}

      {/* Run Tab */}
      {activeTab === 'run' && (
        <div className="space-y-6">
          <div className="section-card">
            <button type="button" onClick={handleOptimize} className="btn-primary">Optimize Inventory</button>
          </div>

      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="stat-card">
              <div className="text-sm text-gray-600 mb-1">Annual Savings</div>
              <div className="text-2xl font-bold text-green-600 flex items-center">
                <TrendingDown className="w-5 h-5 mr-1" />
                {formatCurrency(data.savings)}
              </div>
            </div>
            <div className="stat-card">
              <div className="text-sm text-gray-600 mb-1">Status</div>
              <div className="text-2xl font-bold text-green-600">{data.status}</div>
            </div>
            <div className="stat-card">
              <div className="text-sm text-gray-600 mb-1">Items Optimized</div>
              <div className="text-2xl font-bold text-gray-800 flex items-center">
                <Package className="w-5 h-5 mr-1" />
                {data.items.length}
              </div>
            </div>
          </div>

          {/* Inventory Optimization Table */}
          <div className="table-container">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">SKU Optimization Results</h3>
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Current</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Optimal</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Reorder Point</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">EOQ</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Change</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.items.map((item) => {
                  const change = ((item.optimal - item.current) / item.current * 100).toFixed(1);
                  const isIncrease = item.optimal > item.current;
                  return (
                    <tr key={item.sku} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-mono text-gray-900">{item.sku}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{item.product}</td>
                      <td className="px-6 py-4 text-sm text-right text-gray-900">{item.current.toLocaleString()}</td>
                      <td className="px-6 py-4 text-sm text-right font-semibold text-blue-600">{item.optimal.toLocaleString()}</td>
                      <td className="px-6 py-4 text-sm text-right text-gray-900">{item.reorder.toLocaleString()}</td>
                      <td className="px-6 py-4 text-sm text-right text-gray-900">{item.eoq.toLocaleString()}</td>
                      <td className="px-6 py-4 text-center">
                        <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${
                          isIncrease ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                        }`}>
                          {isIncrease ? <ArrowUpCircle className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
                          {change}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {data.llm_explanation && (
            <div className="section-card bg-purple-50 border-l-4 border-purple-500">
              <h3 className="text-lg font-bold text-purple-900 mb-3">AI Analysis</h3>
              <p className="text-gray-700 leading-relaxed whitespace-pre-line">{data.llm_explanation}</p>
            </div>
          )}

          {data.recommendations && (
            <div className="success-section">
              <h3 className="text-lg font-bold text-green-900 mb-3">Optimization Recommendations</h3>
              <ol className="space-y-2">
                {data.recommendations.map((rec, idx) => (
                  <li key={idx} className="flex items-start">
                    <span className="font-bold text-green-700 mr-2">{idx + 1}.</span>
                    <span className="text-gray-700">{rec}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </>
      )}
        </div>
      )}

      {/* Simulation Tab */}
      {activeTab === 'simulation' && (
        <InventorySimulation />
      )}
    </div>
  );
};

export default InventoryModule;
